/* eslint-disable no-irregular-whitespace */

/**
 * @Description Admin module saga/watcher file.
 * @FileName moduleWatcher.js
 * @Author Arnab Sutar - sutaarn
 * @CreatedOn 22 January, 2021 08:51:22
 * @IssueID - 308
 */
import { takeEvery, put, call, all } from 'redux-saga/effects';
import FileSaver from 'file-saver';
import { interfaceMappingActionType } from '../actions/actionTypes';

import { formatResponse, exportErrorMsg, Logger, processError, renderFileName } from '../../../../common/util';
import invokeApi from '../../../../common/apiManagement/invokeApi';
import { getAction } from '../../../../../stateManagement/reduxUtil';
import {
  processingStarted, processingCompleted,
} from '../../../../common/redux/actions/commonActions';
import moduleAPI from '../../config/apiConfig';
import { globalConstants } from '../../../../common/moduleConstants';

const getResetUpsertValue = (responseData, err) => ({
  upsertRequest: null,
  upsertRequestError: err ? processError(err) : null,
  upsertRequestedOn: null,
  upsertRequestProcessedOn: responseData ? new Date() : null,
  upsertData: responseData,
});

/**
  * This generator function will invoke the api
  * @param {object} action - action which will request for IM Export Listing
  */
export function* getIMExportList(action) {
  yield all([
    put(processingStarted()),
    put(getAction(interfaceMappingActionType.RESET_SERVER_VALUE, null)),
  ]);
  let resetServerValue = null;
  try {
    const responseData = yield call(
      invokeApi,
      moduleAPI.ADMINISTRATION_IM_EXPORT_LIST,
      action.data,
    );
    const filename = renderFileName(responseData);
    const urlData = window.URL.createObjectURL(new Blob([responseData.data]));
    FileSaver(urlData, filename);
    yield put(processingCompleted());
  } catch (err) {
    if (err.response.status === globalConstants.errorCode402 || err.response.status === globalConstants.errorCode403) {
      const exportMaxLimitErrorObj = exportErrorMsg(err.response.status);
      resetServerValue = { serverRequestError: exportMaxLimitErrorObj };
    } else {
      resetServerValue = { serverRequestError: processError(err) };
    }
    yield all([
      put(getAction(interfaceMappingActionType.RESET_SERVER_VALUE, resetServerValue)),
      put(processingCompleted()),
    ]);
  }
}

export function* getIMList(action) {
  Logger.verbose(`Action Detail For Get Gen Edit Type List : ${JSON.stringify(action)}`);
  yield all([
    put(processingStarted()),
    put(getAction(interfaceMappingActionType.RESET_SERVER_VALUE, null)),
    put(getAction(interfaceMappingActionType.LOG_IM_LIST_REQUEST, action.data)),
  ]);
  let resetServerValue = { serverRequestError: null };
  try {
    const responseData = yield call(invokeApi, moduleAPI.ADMINISTRATION_IM_LIST, action.data);
    yield all([
      put(getAction(interfaceMappingActionType.GET_IM_LIST_RESPONSE, responseData)),
      put(processingCompleted()),
    ]);
  } catch (err) {
    resetServerValue = { serverRequestError: processError(err) };
    yield all([
      put(getAction(interfaceMappingActionType.RESET_SERVER_VALUE, resetServerValue)),
      put(getAction(interfaceMappingActionType.GET_IM_LIST_ERROR, err)),
      put(processingCompleted()),
    ]);
  }
}
/**
 * This generator function will invoke the api
 * @param {object} action - action which will insert data for New IM by ID
 */
export function* getIMbyID(action) {
  Logger.verbose(`Action Detail For IM by ID : ${JSON.stringify(action)}`);
  yield all([
    put(processingStarted()),
    put(getAction(interfaceMappingActionType.RESET_SERVER_VALUE, null)),
    put(getAction(interfaceMappingActionType.LOG_IM_BY_ID_REQUEST, action.data)),
  ]);
  let resetServerValue = { serverRequestError: null };
  try {
    const responseData = yield call(invokeApi, moduleAPI.ADMINISTRATION_IM_BY_ID, action.data);
    yield all([
      put(getAction(interfaceMappingActionType.GET_IM_BY_ID_RESPONSE, formatResponse({ action, responseData }))),
      put(processingCompleted()),
    ]);
  } catch (err) {
    resetServerValue = { serverRequestError: processError(err) };
    yield all([
      put(getAction(interfaceMappingActionType.RESET_SERVER_VALUE, resetServerValue)),
      put(getAction(interfaceMappingActionType.GET_IM_BY_ID_ERROR, err)),
      put(processingCompleted()),
    ]);
  }
}
/**
 * This generator function will invoke the api
 * @param {object} action - action which will insert data for new Plan code
 */
export function* insertIm(action) {
  Logger.verbose(`Action Detail For Interface Mapping List : ${JSON.stringify(action)}`);
  yield all([
    put(processingStarted()),
  ]);
  let resetUpsertValue = null;
  try {
    const responseData = yield call(
      invokeApi,
      moduleAPI.ADMINISTRATION_IM_INSERT,
      action.data.formatObjectAdd,
    );
    if (responseData) {
      resetUpsertValue = getResetUpsertValue(responseData, null);
      yield all([
        put(getAction(interfaceMappingActionType.RESET_UPSERT_VALUE, resetUpsertValue)),
        put(getAction(interfaceMappingActionType.GET_IM_LIST_REQUEST, action.data.reqObject)),
        put(processingCompleted()),
      ]);
    }
  } catch (err) {
    resetUpsertValue = getResetUpsertValue(null, err);
    yield all([
      put(getAction(interfaceMappingActionType.RESET_UPSERT_VALUE, resetUpsertValue)),
      put(processingCompleted())]);
  }
}
/**
 * This generator function will invoke the api
 * @param {object} action - action which will update data for existing Plan code
 */
export function* updateIm(action) {
  yield all([
    put(processingStarted()),
  ]);
  let resetUpsertValue = null;
  try {
    const responseData = yield call(
      invokeApi,
      moduleAPI.ADMINISTRATION_IM_UPDATE,
      action.data.formatObjectAdd,
    );
    if (responseData) {
      resetUpsertValue = getResetUpsertValue(responseData, null);
      yield put(getAction(interfaceMappingActionType.RESET_UPSERT_VALUE, resetUpsertValue));
      yield put(getAction(interfaceMappingActionType.GET_IM_LIST_REQUEST, action.data.reqObject));
    }
    yield all([
      put(processingCompleted()),
    ]);
  } catch (err) {
    resetUpsertValue = {
      upsertRequest: null,
      upsertRequestError: processError(err),
      upsertRequestedOn: null,
      upsertRequestProcessedOn: null,
    };
    yield all([
      put(getAction(interfaceMappingActionType.RESET_UPSERT_VALUE, resetUpsertValue)),
      put(processingCompleted()),
    ]);
  }
}
export function* deleteIm(action) {
  yield all([put(processingStarted())]);
  let resetDeleteValue = null;
  try {
    const responseData = yield call(
      invokeApi,
      moduleAPI.ADMINISTRATION_IM_DELETE,
      action.data.formatObject,
    );
    if (responseData) {
      resetDeleteValue = {
        deleteRequest: null,
        deleteRequestError: null,
        deleteRequestedOn: null,
        deleteRequestProcessedOn: new Date(),
        deleteData: responseData,
      };
      yield put(getAction(interfaceMappingActionType.RESET_DELETE_VALUE, resetDeleteValue));
      yield put(
        getAction(interfaceMappingActionType.GET_IM_LIST_REQUEST, action.data.reqObject),
      );
    }
    yield all([
      put(getAction(interfaceMappingActionType.GET_IM_LIST_RESPONSE, responseData)),
      put(processingCompleted()),
    ]);
  } catch (err) {
    resetDeleteValue = {
      deleteRequest: null,
      deleteRequestError: processError(err),
      upsertRequestError: processError(err),
      deleteRequestedOn: new Date(),
      deleteRequestProcessedOn: null,
      deleteData: null,
    };
    yield all([
      put(getAction(interfaceMappingActionType.RESET_UPSERT_VALUE, resetDeleteValue)),
      put(getAction(interfaceMappingActionType.RESET_DELETE_VALUE, resetDeleteValue)),
      put(processingCompleted()),
    ]);
  }
}
/**
  * This generator function will invoke the api
  * @param {object} action - action which will request for LookUps Data
  */
export function* getLookUpsDataIm(action) {
  yield put(processingStarted());
  try {
    const responseData = yield call(invokeApi, moduleAPI.IM_LOOKUPS_DATA, action.data);
    yield all([
      put(getAction(interfaceMappingActionType.RESET_IM_LOOKUPDATA, responseData)),
      put(processingCompleted()),
    ]);
  } catch (err) {
    // put request failed action
    yield all([
      put(getAction(interfaceMappingActionType.RESET_IM_LOOKUPDATA, null)),
      put(processingCompleted()),
    ]);
  }
}

function* moduleWatcher() {
  // Interface Mapping
  yield takeEvery(interfaceMappingActionType.GET_IM_LIST_REQUEST, getIMList);
  yield takeEvery(interfaceMappingActionType.GET_IM_BY_ID_REQUEST, getIMbyID);
  yield takeEvery(interfaceMappingActionType.INSERT_IM_REQUEST, insertIm);
  yield takeEvery(interfaceMappingActionType.UPDATE_IM_REQUEST, updateIm);
  yield takeEvery(interfaceMappingActionType.DELETE_IM_REQUEST, deleteIm);
  yield takeEvery(interfaceMappingActionType.GET_IM_LOOKUP_REQUEST, getLookUpsDataIm);
  yield takeEvery(interfaceMappingActionType.GET_IM_EXPORT_REQUEST, getIMExportList);
}
export default moduleWatcher;
